import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Agents } from '@air/api';
import {
  type ListAgentSessionArtifactsResponse,
  type SessionArtifact,
  type UpdateAgentSessionArtifactRequest,
} from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import {
  isPrivateWorkspaceAgentSessionArtifactsListKey,
  PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactsList';

export type UpdateWorkspaceAgentSessionArtifactPinParams = {
  artifactId: string;
  pinned: boolean;
  sessionId: string;
  type?: 'assetList' | 'boardList' | 'searchFilter';
  workspaceId: string;
};

export const usePrivateWorkspaceAgentSessionArtifactUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      artifactId,
      pinned,
      sessionId,
      type = 'assetList',
      workspaceId,
    }: UpdateWorkspaceAgentSessionArtifactPinParams) => {
      const updateBody = ((): UpdateAgentSessionArtifactRequest => {
        if (type === 'boardList') {
          return {
            agentType: 'workspace',
            artifactId,
            pinned,
            sessionId,
            type: 'boardList',
            workspaceId,
          };
        }
        if (type === 'searchFilter') {
          return {
            agentType: 'workspace',
            artifactId,
            pinned,
            sessionId,
            type: 'searchFilter',
            workspaceId,
          };
        }

        return {
          agentType: 'workspace',
          artifactId,
          pinned,
          sessionId,
          type: 'assetList',
          workspaceId,
        };
      })();

      return callAirApi(() => Agents.Sessions.Artifacts.update(updateBody));
    },

    onMutate: async ({ artifactId, pinned, sessionId, type = 'assetList', workspaceId }) => {
      await queryClient.cancelQueries({
        queryKey: [PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY],
      });

      const now = new Date().toISOString();
      const optimisticBase = {
        createdAt: now,
        id: artifactId,
        pinned,
        sessionId,
        sourceArtifactId: null,
        updatedAt: now,
        workspaceId,
      };
      const optimisticArtifact: SessionArtifact = (() => {
        if (type === 'boardList') {
          return { ...optimisticBase, summary: {}, type: 'boardList' };
        }
        if (type === 'searchFilter') {
          return { ...optimisticBase, summary: {}, type: 'searchFilter' };
        }

        return { ...optimisticBase, summary: {}, type: 'assetList' };
      })();

      queryClient.setQueriesData<ListAgentSessionArtifactsResponse>(
        {
          predicate: (query) =>
            isPrivateWorkspaceAgentSessionArtifactsListKey(query.queryKey) &&
            query.queryKey[1].sessionId === sessionId &&
            query.queryKey[1].workspaceId === workspaceId &&
            query.queryKey[1].pinned === true,
        },
        (old) => {
          const base: ListAgentSessionArtifactsResponse = old ?? {
            data: [],
            pagination: { cursor: null, hasMore: false },
          };

          if (!pinned) {
            return {
              ...base,
              data: base.data.filter((item) => item.id !== artifactId).map((item) => ({ ...item })),
            };
          }

          const existingIndex = base.data.findIndex((item) => item.id === artifactId);

          if (existingIndex >= 0) {
            return {
              ...base,
              data: base.data.map((item) => (item.id === artifactId ? { ...item, pinned: true } : item)),
            };
          }

          return {
            ...base,
            data: [optimisticArtifact, ...base.data],
          };
        },
      );

      queryClient.setQueriesData<ListAgentSessionArtifactsResponse>(
        {
          predicate: (query) =>
            isPrivateWorkspaceAgentSessionArtifactsListKey(query.queryKey) &&
            query.queryKey[1].sessionId === sessionId &&
            query.queryKey[1].workspaceId === workspaceId &&
            query.queryKey[1].pinned !== true,
        },
        (old) => {
          if (!old) {
            return old;
          }

          return {
            ...old,
            data: old.data.map((item) => (item.id === artifactId ? { ...item, pinned } : item)),
          };
        },
      );
    },

    onSettled: (_data, _error, { sessionId, workspaceId }) => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          isPrivateWorkspaceAgentSessionArtifactsListKey(query.queryKey) &&
          query.queryKey[1].sessionId === sessionId &&
          query.queryKey[1].workspaceId === workspaceId,
      });
    },
  });
};
